import { useEffect, useState, useRef, ReactNode } from 'react';
import { XMarkIcon } from '@heroicons/react/24/outline';

interface InfoToastProps {
  message: string;
  icon?: ReactNode;
  onClose: () => void;
  duration?: number; // milliseconds, default 6000. Set to 0 for no auto-close
}

/**
 * InfoToast - Informational toast notification
 * 
 * Follows the same design patterns as GuidedHintToast and HintSuccessToast:
 * - Slide-in animation from top
 * - Progress bar for auto-dismiss
 * - Swipe-right to dismiss (mobile friendly)
 * - Consistent styling with the app's design system
 */
export default function InfoToast({ message, icon, onClose, duration = 6000 }: InfoToastProps) {
  const [isVisible, setIsVisible] = useState(false);
  const [progress, setProgress] = useState(100);
  const [touchStart, setTouchStart] = useState<{ x: number; y: number } | null>(null);
  const [swipeOffset, setSwipeOffset] = useState(0);
  const [isDismissing, setIsDismissing] = useState(false);
  const toastRef = useRef<HTMLDivElement>(null);
  const onCloseRef = useRef(onClose);

  useEffect(() => {
    onCloseRef.current = onClose;
  }, [onClose]);

  useEffect(() => {
    // Trigger entrance animation
    setTimeout(() => setIsVisible(true), 10);

    // Skip auto-close if duration is 0
    if (duration === 0) return;

    // Progress bar animation
    const startTime = Date.now();
    const interval = setInterval(() => {
      const elapsed = Date.now() - startTime;
      const remaining = Math.max(0, 100 - (elapsed / duration) * 100);
      setProgress(remaining);

      if (remaining === 0) {
        clearInterval(interval);
      }
    }, 50);

    // Auto-close after duration
    const timeout = setTimeout(() => {
      setIsDismissing(true);
      setIsVisible(false);
      setTimeout(() => onCloseRef.current(), 300); // Wait for exit animation
    }, duration);

    return () => {
      clearInterval(interval);
      clearTimeout(timeout);
    };
  }, [duration, message]);

  const handleClose = () => {
    setIsDismissing(true);
    setIsVisible(false);
    setTimeout(() => onCloseRef.current(), 300); // Wait for exit animation
  };

  // Handle swipe right to dismiss (mobile-friendly)
  const handleTouchStart = (e: React.TouchEvent) => {
    setTouchStart({
      x: e.touches[0].clientX,
      y: e.touches[0].clientY
    });
  };

  const handleTouchMove = (e: React.TouchEvent) => {
    if (!touchStart) return;
    
    const currentX = e.touches[0].clientX;
    const diffX = currentX - touchStart.x;
    
    // Only allow swipe right (positive offset)
    if (diffX > 0) {
      setSwipeOffset(diffX);
    }
  };

  const handleTouchEnd = (e: React.TouchEvent) => {
    if (touchStart === null) return;
    
    const touchEnd = {
      x: e.changedTouches[0].clientX,
      y: e.changedTouches[0].clientY
    };
    
    const diffX = touchEnd.x - touchStart.x;
    const diffY = touchEnd.y - touchStart.y;
    
    // Swipe right (diffX > 80px and mostly horizontal)
    if (diffX > 80 && Math.abs(diffY) < 50) {
      handleClose();
    } else {
      // Reset position if swipe wasn't far enough
      setSwipeOffset(0);
    }
    
    setTouchStart(null);
  };

  return (
    <div
      ref={toastRef}
      onTouchStart={handleTouchStart}
      onTouchMove={handleTouchMove}
      onTouchEnd={handleTouchEnd}
      className={`fixed top-3 md:top-4 left-1/2 -translate-x-1/2 w-[calc(100%-1.5rem)] md:w-[calc(100%-2rem)] max-w-sm md:max-w-md bg-gradient-to-r from-slate-50/95 to-gray-50/95 backdrop-blur-sm border border-slate-300/70 rounded-xl shadow-xl md:shadow-2xl z-[9999] transition-all ease-out overflow-hidden ${
        isDismissing ? 'duration-300' : swipeOffset > 0 ? 'duration-75' : 'duration-300'
      }`}
      style={{
        transform: `translate(-50%, ${isVisible ? '0' : '-100%'}) translateX(${swipeOffset}px)`,
        opacity: isVisible ? Math.max(0, 1 - swipeOffset / 200) : 0
      }}
      role="alert"
      aria-live="polite"
    >
      {/* Progress bar - thinner in mobile */}
      {duration > 0 && (
        <div 
          className={`absolute bottom-0 left-0 h-0.5 md:h-1 bg-slate-400/90 ${progress < 100 ? 'rounded-br-xl' : ''} rounded-bl-xl transition-all duration-100`} 
          style={{ width: `${progress}%` }} 
        />
      )}

      {/* Swipe indicator (subtle visual cue for horizontal swipe) */}
      <div className="absolute top-2 left-1/2 -translate-x-1/2 w-12 h-1 bg-gray-300 rounded-full" />

      <div className="p-3 md:p-4 pt-5 pr-10 md:pr-12">
        <div className="flex items-start gap-3">
          {/* Icon */}
          {icon && (
            <div className="flex-shrink-0 mt-0.5">
              <div className="w-7 h-7 md:w-8 md:h-8 bg-slate-500 rounded-full flex items-center justify-center">
                {icon}
              </div>
            </div>
          )}

          {/* Content */}
          <div className="flex-1 min-w-0">
            <p className="text-xs md:text-sm text-gray-800 leading-relaxed">{message}</p>
          </div>

          {/* Close button */}
          <button
            onClick={handleClose}
            className="absolute top-4 md:top-5 right-2 md:right-3 text-gray-500 hover:text-gray-700 transition-colors p-1"
            aria-label="Close notification"
          >
            <XMarkIcon className="w-5 h-5 md:w-6 md:h-6" />
          </button>
        </div>
      </div>
    </div>
  );
}
